import { useState, useEffect } from 'react';
import { adminAPI } from '../services/api';
import { SellerIntake, FieldConfig } from '../types';

interface FieldSettingsTabProps {
  grouped: Record<string, FieldConfig[]>;
  loading: boolean;
  error: string | null;
  togglingId: number | null;
  onToggle: (field: FieldConfig) => void;
}

function FieldSettingsTab({ grouped, loading, error, togglingId, onToggle }: FieldSettingsTabProps) {
  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-6 flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-primary mx-auto mb-3"></div>
          <p className="text-gray-500 text-sm">Loading field settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800 text-sm">{error}</div>
      )}

      <p className="text-sm text-gray-600">
        Toggle which fields sellers must complete before submitting the form. Changes take effect immediately for new form sessions.
      </p>

      {Object.entries(grouped).map(([sectionLabel, fields]) => {
        const requiredCount = fields.filter(f => f.is_required === 1).length;

        return (
          <div key={sectionLabel} className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 bg-gray-50 border-b border-gray-200">
              <h3 className="font-semibold text-brand-primary text-sm uppercase tracking-wide">
                {sectionLabel}
              </h3>
              {requiredCount > 0 && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-brand-coral text-white">
                  {requiredCount} required
                </span>
              )}
            </div>

            <ul className="divide-y divide-gray-100">
              {fields.map((field) => {
                const isRequired = field.is_required === 1;
                const isToggling = togglingId === field.id;

                return (
                  <li key={field.id} className="flex items-center justify-between px-5 py-3">
                    <span className={`text-sm ${isRequired ? 'font-medium text-gray-900' : 'text-gray-600'}`}>
                      {field.field_label}
                    </span>
                    <button
                      onClick={() => onToggle(field)}
                      disabled={isToggling}
                      aria-label={`${isRequired ? 'Make optional' : 'Make required'}: ${field.field_label}`}
                      className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent
                        transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-brand-primary focus:ring-offset-2
                        disabled:opacity-50 disabled:cursor-not-allowed
                        ${isRequired ? 'bg-brand-coral' : 'bg-gray-200'}`}
                    >
                      <span
                        className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0
                          transition duration-200 ease-in-out
                          ${isRequired ? 'translate-x-4' : 'translate-x-0'}`}
                      />
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}

      {Object.keys(grouped).length === 0 && !loading && (
        <div className="bg-white rounded-lg shadow-sm p-12 text-center text-gray-500 text-sm">
          No field configurations found.
        </div>
      )}
    </div>
  );
}

export default function AdminDashboard() {
  const [intakes, setIntakes] = useState<SellerIntake[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedIntake, setSelectedIntake] = useState<SellerIntake | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_intakeData, setIntakeData] = useState<any>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const [activeTab, setActiveTab] = useState<'submissions' | 'field-settings'>('submissions');
  const [fieldConfigs, setFieldConfigs] = useState<Record<string, FieldConfig[]>>({});
  const [fieldConfigsLoading, setFieldConfigsLoading] = useState(false);
  const [fieldConfigsError, setFieldConfigsError] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<number | null>(null);

  useEffect(() => {
    loadIntakes();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  useEffect(() => {
    if (activeTab === 'field-settings') {
      loadFieldConfigs();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const loadIntakes = async () => {
    try {
      setLoading(true);
      setError(null);
      const filters = statusFilter !== 'all' ? { status: statusFilter } : {};
      const data = await adminAPI.getIntakes(filters);
      setIntakes(data);
    } catch (err) {
      setError('Failed to load intakes');
      console.error('Error loading intakes:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadFieldConfigs = async () => {
    try {
      setFieldConfigsLoading(true);
      setFieldConfigsError(null);
      const data = await adminAPI.getFieldConfigs();
      setFieldConfigs(data.grouped);
    } catch (err) {
      setFieldConfigsError('Failed to load field configurations');
      console.error('Error loading field configs:', err);
    } finally {
      setFieldConfigsLoading(false);
    }
  };

  const handleToggleRequired = async (field: FieldConfig) => {
    if (togglingId !== null) return;
    const newValue = field.is_required === 0;
    setTogglingId(field.id);

    setFieldConfigs(prev => {
      const updated = { ...prev };
      updated[field.section_label] = updated[field.section_label].map(f =>
        f.id === field.id ? { ...f, is_required: newValue ? 1 : 0 } : f
      );
      return updated;
    });

    try {
      await adminAPI.updateFieldConfig(field.id, newValue);
    } catch (err) {
      console.error('Error updating field config:', err);
      setFieldConfigs(prev => {
        const reverted = { ...prev };
        reverted[field.section_label] = reverted[field.section_label].map(f =>
          f.id === field.id ? { ...f, is_required: field.is_required } : f
        );
        return reverted;
      });
      setFieldConfigsError('Failed to save. Please try again.');
    } finally {
      setTogglingId(null);
    }
  };

  const loadIntakeDetails = async (intake: SellerIntake) => {
    try {
      setSelectedIntake(intake);
      setIntakeData(null);
    } catch (err) {
      console.error('Error loading intake details:', err);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'not_started':
        return 'bg-gray-100 text-gray-800';
      case 'in_progress':
        return 'bg-brand-primary/10 text-brand-primary';
      case 'submitted':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const copyLink = (intake: SellerIntake) => {
    const link = `${window.location.origin}/intake/${intake.access_token}`;
    navigator.clipboard.writeText(link);
    setCopiedId(intake.id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const getCompletionColor = (percentage: number) => {
    if (percentage >= 80) return 'text-green-600';
    if (percentage >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  if (loading && activeTab === 'submissions') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <h1 className="text-2xl font-bold text-gray-900">Seller Homework Admin</h1>
          <p className="text-sm text-gray-600">Manage and review seller homework submissions</p>
        </div>

        {/* Tab Navigation */}
        <div className="max-w-7xl mx-auto px-4">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('submissions')}
              className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'submissions'
                  ? 'border-brand-primary text-brand-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Submissions
            </button>
            <button
              onClick={() => setActiveTab('field-settings')}
              className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'field-settings'
                  ? 'border-brand-primary text-brand-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Field Settings
            </button>
          </nav>
        </div>
      </div>

      {activeTab === 'field-settings' ? (
        <FieldSettingsTab
          grouped={fieldConfigs}
          loading={fieldConfigsLoading}
          error={fieldConfigsError}
          togglingId={togglingId}
          onToggle={handleToggleRequired}
        />
      ) : (
        <div className="max-w-7xl mx-auto px-4 py-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 text-red-800">
              {error}
            </div>
          )}

          {/* Filters */}
          <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
            <div className="flex items-center gap-4">
              <label className="text-sm font-medium text-gray-700">Filter by status:</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
              >
                <option value="all">All Status</option>
                <option value="not_started">Not Started</option>
                <option value="in_progress">In Progress</option>
                <option value="submitted">Submitted</option>
              </select>
              <button
                onClick={loadIntakes}
                className="px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-primary-dark transition-colors"
              >
                Refresh
              </button>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow-sm p-4">
              <h3 className="text-sm font-medium text-gray-500">Total Intakes</h3>
              <p className="text-2xl font-bold text-gray-900">{intakes.length}</p>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-4">
              <h3 className="text-sm font-medium text-gray-500">Not Started</h3>
              <p className="text-2xl font-bold text-gray-900">
                {intakes.filter(i => i.status === 'not_started').length}
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-4">
              <h3 className="text-sm font-medium text-gray-500">In Progress</h3>
              <p className="text-2xl font-bold text-brand-primary">
                {intakes.filter(i => i.status === 'in_progress').length}
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-4">
              <h3 className="text-sm font-medium text-gray-500">Submitted</h3>
              <p className="text-2xl font-bold text-green-600">
                {intakes.filter(i => i.status === 'submitted').length}
              </p>
            </div>
          </div>

          {/* Intakes Table */}
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Property Address
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Seller
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Completion
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Started
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {intakes.map((intake) => (
                    <tr key={intake.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">
                        {intake.property_address}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {intake.seller_name && <div className="font-medium text-gray-700">{intake.seller_name}</div>}
                        {intake.seller_email
                          ? <div className="text-xs">{intake.seller_email}</div>
                          : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(intake.status)}`}>
                          {intake.status.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <span className={`text-sm font-medium ${getCompletionColor(intake.completion_percentage)}`}>
                            {intake.completion_percentage}%
                          </span>
                          <div className="ml-2 w-16 bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-brand-primary h-2 rounded-full"
                              style={{ width: `${intake.completion_percentage}%` }}
                            />
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(intake.started_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-3">
                        <button
                          onClick={() => copyLink(intake)}
                          className={`${copiedId === intake.id ? 'text-green-600' : 'text-gray-500 hover:text-gray-800'}`}
                          title="Copy magic link"
                        >
                          {copiedId === intake.id ? 'Copied!' : 'Copy Link'}
                        </button>
                        <button
                          onClick={() => loadIntakeDetails(intake)}
                          className="text-brand-primary hover:text-blue-900"
                        >
                          View
                        </button>
                        <button
                          onClick={async () => {
                            if (!window.confirm(`Delete submission for "${intake.property_address}"? This cannot be undone.`)) return;
                            try {
                              await adminAPI.deleteIntake(intake.id);
                              loadIntakes();
                            } catch {
                              alert('Failed to delete submission. Please try again.');
                            }
                          }}
                          className="text-red-500 hover:text-red-700"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {intakes.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-500">No intakes found</p>
              </div>
            )}
          </div>

          {/* Intake Details Modal */}
          {selectedIntake && (
            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
              <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
                <div className="mt-3">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-medium text-gray-900">Intake Details</h3>
                    <button
                      onClick={() => setSelectedIntake(null)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      ×
                    </button>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <h4 className="font-medium text-gray-900">Property Address</h4>
                      <p className="text-gray-600">{selectedIntake.property_address}</p>
                    </div>

                    {(selectedIntake.seller_name || selectedIntake.seller_email) && (
                      <div className="grid grid-cols-2 gap-4">
                        {selectedIntake.seller_name && (
                          <div>
                            <h4 className="font-medium text-gray-900">Seller Name</h4>
                            <p className="text-gray-600">{selectedIntake.seller_name}</p>
                          </div>
                        )}
                        {selectedIntake.seller_email && (
                          <div>
                            <h4 className="font-medium text-gray-900">Seller Email</h4>
                            <p className="text-gray-600">{selectedIntake.seller_email}</p>
                          </div>
                        )}
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <h4 className="font-medium text-gray-900">Status</h4>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(selectedIntake.status)}`}>
                          {selectedIntake.status.replace('_', ' ')}
                        </span>
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">Completion</h4>
                        <p className="text-gray-600">{selectedIntake.completion_percentage}%</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <h4 className="font-medium text-gray-900">Started</h4>
                        <p className="text-gray-600">{formatDate(selectedIntake.started_at)}</p>
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">Last Updated</h4>
                        <p className="text-gray-600">{formatDate(selectedIntake.last_updated_at)}</p>
                      </div>
                    </div>

                    <div>
                      <h4 className="font-medium text-gray-900 mb-1">Magic Link</h4>
                      <p className="text-gray-500 font-mono text-xs break-all bg-gray-50 p-2 rounded">
                        {window.location.origin}/intake/{selectedIntake.access_token}
                      </p>
                    </div>

                    <div className="flex gap-4 pt-2">
                      <button
                        onClick={() => copyLink(selectedIntake)}
                        className={`px-4 py-2 rounded-lg transition-colors ${
                          copiedId === selectedIntake.id
                            ? 'bg-green-600 text-white'
                            : 'bg-gray-600 text-white hover:bg-gray-700'
                        }`}
                      >
                        {copiedId === selectedIntake.id ? 'Copied!' : 'Copy Magic Link'}
                      </button>
                      <a
                        href={`/intake/${selectedIntake.access_token}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-primary-dark transition-colors"
                      >
                        Open Form
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
